import React, {
  Activity,
  Dispatch,
  FunctionComponent,
  memo,
  RefObject,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';

import styles from '../custom-select.module.css';

import Portal from '@ui/portal/portal';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import useSetMenuPosition from '../helpers/use-set-menu-position';
import GlobalContainer from '@ui/container/global-container';
import CustomSelectMenuOption from './custom-select-menu-option';
import Text from '@ui/text/text/text';
import { mergeRefs } from '@/utils/merge-refs';

type CustomSelectMenuProps = {
  menuOpen: boolean;
  menuRef: RefObject<HTMLDivElement | null>;
  menuShellRef: RefObject<HTMLDivElement | null>;
  clipMenuOverflow?: boolean;
  selectRef: RefObject<HTMLDivElement | null>;
  inputRef: RefObject<HTMLInputElement | null>;
  handlePreventDefaultClick: (e: React.MouseEvent<HTMLElement>) => void;
  selectableOptions: TReactSelectOption[];
  selectedValuesSet: Set<string | number>;
  setSelectedOptions: Dispatch<SetStateAction<TReactSelectOption[]>>;
  onAddNewSelect?: (inputValue: string) => void;
  RenderMenuOptionContent?: FunctionComponent<{ option: TReactSelectOption; idx: number; isSelected: boolean }>;
  RenderNoOptionsContent?: FunctionComponent<{ inputValue: string }>;
  menuOptionSelectedIndicatorLabel?: string;
  onSelectOption?: (option: { option: TReactSelectOption; status: 'selected' | 'deselected' | 'add-new' }) => void;
  dynamicMenuPlacement?: boolean;
  virtualizeMenu?: boolean;
  virtuosoRef?: RefObject<VirtuosoHandle | null>;
  isMultiSelect?: boolean;
  metaImageKey?: string | null;
  disableAddNewIfNoInputValue?: boolean;
  onMenuEndReached?: () => void;
  isLoadingOptions?: boolean;
};

const CustomSelectMenu = memo(
  ({
    handlePreventDefaultClick,
    menuOpen,
    menuRef,
    menuShellRef,
    clipMenuOverflow = true,
    selectableOptions,
    setSelectedOptions,
    selectedValuesSet,
    selectRef,
    onAddNewSelect,
    RenderMenuOptionContent,
    RenderNoOptionsContent,
    inputRef,
    menuOptionSelectedIndicatorLabel,
    onSelectOption,
    dynamicMenuPlacement,
    virtualizeMenu,
    virtuosoRef,
    isMultiSelect = true,
    metaImageKey,
    disableAddNewIfNoInputValue,
    onMenuEndReached,
    isLoadingOptions,
  }: CustomSelectMenuProps) => {
    // When clipping, the shell is the positioned element (it owns the CSS
    // position vars + the rounded clip). Otherwise .menu positions itself.
    useSetMenuPosition({
      controlRef: selectRef,
      menuRef: clipMenuOverflow ? menuShellRef : menuRef,
      pause: !menuOpen,
      recalculate: menuOpen ? 1 : 0,
      dynamicMenuPlacement,
    });

    const ShellWrapper: React.ElementType = clipMenuOverflow ? 'div' : React.Fragment;
    const shellWrapperProps = clipMenuOverflow
      ? {
          ref: mergeRefs(menuShellRef),
          className: `${styles['menu-shell']} ${menuOpen ? styles['menu-open'] : ''}`,
        }
      : {};

    const [inputHasValue, setInputHasValue] = useState(false);
    useEffect(() => {
      if (!menuOpen) return;
      const inputEl = inputRef?.current;
      if (!inputEl) return;

      const handleChange = (e: Event) => {
        const hasVal = !!(e.target as HTMLInputElement)?.value?.trim();
        setInputHasValue(hasVal);
      };

      inputEl.addEventListener('input', handleChange);

      return () => {
        inputEl.removeEventListener('input', handleChange);
      };
    }, [inputRef, menuOpen]);

    return (
      <Portal
        className="position-fixed top-0 height-0 overflow-hidden"
        inert={!menuOpen}
        style={{ zIndex: 1000 }}
        data-component="CustomSelectMenu"
      >
        <ShellWrapper {...shellWrapperProps}>
          <GlobalContainer
            className={`${styles.menu} ${clipMenuOverflow ? styles['menu-in-shell'] : ''} ${menuOpen ? styles['menu-open'] : ''}`}
            onPointerDown={handlePreventDefaultClick}
            mode="GlobalCont1"
            ref={menuRef}
            role="menu"
            inert={!menuOpen}
          >
            <Activity mode={!selectableOptions.length && !isLoadingOptions ? 'visible' : 'hidden'}>
              {RenderNoOptionsContent ? (
                <RenderNoOptionsContent inputValue={inputRef?.current?.value || ''} />
              ) : (
                <div className="padding-16">
                  <Text className="S2" design="GlobalIcoText3">
                    No options found
                  </Text>
                </div>
              )}
            </Activity>

            {menuOpen ? (
              virtualizeMenu ? (
                <Virtuoso
                  ref={virtuosoRef}
                  scrollerRef={mergeRefs(menuShellRef)}
                  customScrollParent={menuRef.current ?? undefined}
                  // adding a min initial render count of 10 to prevent dead locks between
                  // virtuoso's height and the parent matx-height. Otherwise virtuoso won't
                  // properly render because it needs a scroll parent to have a greater than 0 height
                  initialItemCount={Math.min(selectableOptions.length, 10)}
                  data={selectableOptions}
                  endReached={onMenuEndReached}
                  computeItemKey={(_idx, option) => option.value}
                  itemContent={(idx, option) => (
                    <CustomSelectMenuOption
                      option={option}
                      idx={idx}
                      handlePreventDefaultClick={handlePreventDefaultClick}
                      setSelectedOptions={setSelectedOptions}
                      RenderMenuOptionContent={RenderMenuOptionContent}
                      selectRef={selectRef}
                      isSelected={selectedValuesSet.has(option.value)}
                      selectedValuesSet={selectedValuesSet}
                      menuOptionSelectedIndicatorLabel={menuOptionSelectedIndicatorLabel}
                      onSelectOption={onSelectOption}
                      isMultiSelect={isMultiSelect}
                      metaImageKey={metaImageKey}
                    />
                  )}
                />
              ) : (
                selectableOptions.map((option, idx) => (
                  <CustomSelectMenuOption
                    key={option.value}
                    option={option}
                    idx={idx}
                    handlePreventDefaultClick={handlePreventDefaultClick}
                    setSelectedOptions={setSelectedOptions}
                    RenderMenuOptionContent={RenderMenuOptionContent}
                    selectRef={selectRef}
                    isSelected={selectedValuesSet.has(option.value)}
                    selectedValuesSet={selectedValuesSet}
                    menuOptionSelectedIndicatorLabel={menuOptionSelectedIndicatorLabel}
                    onSelectOption={onSelectOption}
                    isMultiSelect={isMultiSelect}
                    metaImageKey={metaImageKey}
                  />
                ))
              )
            ) : null}

            <Activity mode={isLoadingOptions ? 'visible' : 'hidden'}>
              <div className="padding-16">
                <Text className="S2" design="GlobalIcoText3">
                  Loading...
                </Text>
              </div>
            </Activity>

            <Activity mode={onAddNewSelect ? 'visible' : 'hidden'}>
              <div
                role="option"
                aria-label="Add new option"
                aria-disabled={!!(disableAddNewIfNoInputValue && !inputHasValue)}
                className={`${styles.menuOption} ${styles.addNewOption} keep-open`}
                onClick={(e) => {
                  handlePreventDefaultClick(e);
                  if (disableAddNewIfNoInputValue && !inputHasValue) {
                    return;
                  }
                  onAddNewSelect?.(inputRef?.current?.value || '');
                  onSelectOption?.({
                    option: { label: inputRef?.current?.value || '', value: inputRef?.current?.value || '' },
                    status: 'add-new',
                  });
                  const target = e.target;
                  if (target instanceof HTMLElement) {
                    target.setAttribute('data-menu-list-option-active', 'false');
                  }
                }}
                data-menu-list-option
                data-menu-list-option-active="false"
                data-menu-list-option-idx={selectableOptions.length}
                data-menu-list-option-value={'_add_new_'}
                data-menu-list-option-add-new
              >
                <Text opacity={disableAddNewIfNoInputValue && !inputHasValue ? 0.6 : 1} design="LocalLinkPrim">
                  + Add New
                </Text>
              </div>
            </Activity>
          </GlobalContainer>
        </ShellWrapper>
      </Portal>
    );
  }
);

CustomSelectMenu.displayName = 'CustomSelectMenu';

export default CustomSelectMenu;
